import { z } from 'zod';

import { PaymentDueId, PaymentId } from '../BrandedIds';
import { CompanyPrincipalLateFeeSettings } from '../company/FeeSettings';
import { InvestorRemittanceSplit } from '../loan/InvestorRemittanceSplit';
import { PaymentBreakdown } from '../loan/PaymentBreakdown';
import { zodTemporalPlainDate } from '../Temporal';
import { zodBrandedUuid, zodDateOrString } from '../utils/Zod';
import { BaseLoanEvent } from './BaseLoanEvent';

export const FeeType = z.enum([
  'LATE',
  'DRAW',
  'EXTENSION',
  'PAYOFF',
  'PREPAYMENT_PENALTY',
  'LEGAL',
  'LIEN_RELEASE',
  'PENDING_PAYMENT_AT_PAYOFF',
  'OTHER',
  'NON_SUFFICIENT_FUNDS',
  'TERMINATION',
  'PRINCIPAL_LATE',
  // Elite fees
  'ADMIN',
  'DEBOARDING',
  'LOAN_MODIFICATION',
  'RECORDING',
  'VERIFICATION_OF_MORTGAGE',
  'WIRE',
  'TAX',
  'PAYOFF_REVISION',
  'DOCUMENT',
  'CREDIT_CARD_PROCESSING',
]);
export type FeeType = z.infer<typeof FeeType>;

export const FeeStatus = z.enum(['CHARGED', 'CANCELLED', 'PAID', 'WAIVED', 'CAPITALIZED']);
export type FeeStatus = z.infer<typeof FeeStatus>;

export const AdvanceType = z.enum(['CORPORATE']);
export type AdvanceType = z.infer<typeof AdvanceType>;

export const AdvanceCategory = z.enum([
  'OTHER',
  'BANKRUPTCY',
  'FORECLOSURE',
  'PROPERTY_PRESERVATION',
  'PROPERTY_INSPECTION_COST',
  'BROKER_PRICE_OPINION_COST',
  'APPRAISAL_COST',
  'TAXES',
  'TITLE_SEARCH',
  'PROTECTIVE_ADVANCE',
  'FORCE_PLACED_INSURANCE',
  'ATTORNEY_FEE',
  'AGENT_FEE',
]);
export type AdvanceCategory = z.infer<typeof AdvanceCategory>;

export const PrincipalLateFeeAutomationParameters = z.object({
  type: z.literal('principalLate'),
  forMaturityDate: zodTemporalPlainDate,
  settings: CompanyPrincipalLateFeeSettings,
});
export type PrincipalLateFeeAutomationParameters = z.infer<typeof PrincipalLateFeeAutomationParameters>;
export const FeeAutomationParameters = PrincipalLateFeeAutomationParameters; // todo union this when we have more

export const FeeLineItemInput = z.object({
  amount: z.number(),
  type: FeeType,
  name: z.string().optional(),
  investorRemittanceSplit: InvestorRemittanceSplit.optional(),
  paymentId: zodBrandedUuid<PaymentId>().optional(),
  paymentBreakdowns: PaymentBreakdown.array().optional(),
  automationParameters: FeeAutomationParameters.optional(),
});
export type FeeLineItemInput = z.infer<typeof FeeLineItemInput>;

export const AdvanceConfigInput = z.object({
  interestRate: z.number(),
  advanceType: AdvanceType,
  advanceCategory: AdvanceCategory,
  advanceCategoryDetail: z.string().optional(), // only available if advanceCategory is OTHER
});
export type AdvanceConfigInput = z.infer<typeof AdvanceConfigInput>;

export const ChargeBaseEvent = BaseLoanEvent.extend({
  isDeferred: z.boolean(),
  comment: z.string().optional(),
});

/**
 * @description This event is used to create a fee.
 * @param paymentDueId - The payment due id. Used to track automated fees.
 * @param chargeDate - The date to apply the fee on. This was added to handle odd cases where
 * there isn't a statement to add the fee to (e.g., fees after maturity).
 * @param lineItems - The line items.
 */
export const CreateFeeEvent = ChargeBaseEvent.extend({
  type: z.literal('createFee'),
  paymentDueId: zodBrandedUuid<PaymentDueId>().optional(), // used to track automated fees
  chargeDate: zodTemporalPlainDate.optional(),
  lineItems: z.array(FeeLineItemInput),
});

export const CreateAdvanceEvent = ChargeBaseEvent.extend({
  type: z.literal('createAdvance'),
  advanceConfig: AdvanceConfigInput.optional(),
  advanceInterestUpdatedAt: zodDateOrString.optional(),
  recipient: z.string().optional(),
  chargeDate: zodDateOrString,
  advancePrincipal: z.number(),
  status: FeeStatus,
  investorRemittanceSplit: InvestorRemittanceSplit.optional(),
});

export type CreateFeeEvent = z.infer<typeof CreateFeeEvent>;

export type CreateAdvanceEvent = z.infer<typeof CreateAdvanceEvent>;
